import React from 'react';
import { MapPinIcon, LinkIcon, ShieldIcon, AwardIcon, SendIcon } from 'lucide-react';
import Avatar from '../shared/Avatar';
import Button from '../shared/Button';
interface ProfileHeaderProps {
  profile: {
    name: string;
    username: string;
    avatar: string;
    coverPhoto: string;
    bio: string;
    verified: boolean;
    role: string;
    sport: string;
    location?: string;
    website?: string;
    stats: {
      recommendations: number;
      postViews: number;
      followers: number;
      solicitations: number;
    };
    achievements?: {
      title: string;
      icon: string;
    }[];
    verifications?: {
      title: string;
    }[];
    currentTeam?: {
      name: string;
      logo: string;
    };
  };
}
const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  profile
}) => {
  return <div data-id="element-431">
      {/* Cover Photo */}
      <div className="h-36 relative" data-id="element-432">
        <img src={profile.coverPhoto} alt="Cover" className="w-full h-full object-cover" data-id="element-433" />
        <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-black/40 to-transparent" data-id="element-434"></div>
      </div>
      {/* Profile Info */}
      <div className="px-4 relative pb-4 border-b border-gray-100" data-id="element-435">
        <div className="flex justify-between" data-id="element-436">
          <div className="absolute -top-12 border-4 border-white rounded-full shadow-md" data-id="element-437">
            <Avatar src={profile.avatar} size="xl" sport={profile.sport} data-id="element-438" />
          </div>
          <div className="ml-auto flex space-x-2 mt-2" data-id="element-439">
            <Button variant="outline" size="sm" data-id="element-440">
              Message
            </Button>
            <Button variant="primary" size="sm" data-id="element-441">
              Follow
            </Button>
            <Button variant="secondary" size="sm" icon={<SendIcon size={14} data-id="element-443" />} data-id="element-442">
              Solicitation
            </Button>
          </div>
        </div>
        {/* Name and verification */}
        <div className="mt-10" data-id="element-444">
          <div className="flex items-center" data-id="element-445">
            <h1 className="text-xl font-bold" data-id="element-446">{profile.name}</h1>
            {profile.verified && <svg className="w-5 h-5 text-green-500 ml-1" fill="currentColor" viewBox="0 0 20 20" data-id="element-447">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-448"></path>
              </svg>}
          </div>
          <p className="text-gray-600 text-sm" data-id="element-449">@{profile.username}</p>
          {/* Role and team */}
          <div className="flex items-center mt-1 text-sm" data-id="element-450">
            <span className="font-medium text-green-600" data-id="element-451">{profile.role}</span>
            {profile.currentTeam && <>
                <span className="mx-1 text-gray-400" data-id="element-452">•</span>
                <div className="flex items-center" data-id="element-453">
                  <img src={profile.currentTeam.logo} alt={profile.currentTeam.name} className="w-4 h-4 mr-1 rounded-full" data-id="element-454" />
                  <span data-id="element-455">{profile.currentTeam.name}</span>
                </div>
              </>}
          </div>
        </div>
        {/* Bio */}
        <p className="text-sm mt-3" data-id="element-456">{profile.bio}</p>
        {/* Location and website */}
        <div className="flex flex-col mt-3" data-id="element-457">
          {profile.location && <div className="flex items-center text-sm text-gray-600 mb-1" data-id="element-458">
              <MapPinIcon size={14} className="mr-1" data-id="element-459" />
              <span data-id="element-460">{profile.location}</span>
            </div>}
          {profile.website && <div className="flex items-center text-sm text-green-600" data-id="element-461">
              <LinkIcon size={14} className="mr-1" data-id="element-462" />
              <a href={profile.website} target="_blank" rel="noopener noreferrer" data-id="element-463">
                {profile.website.replace(/(^\w+:|^)\/\//, '')}
              </a>
            </div>}
        </div>
        {/* Verifications */}
        {profile.verifications && profile.verifications.length > 0 && <div className="flex flex-wrap mt-3" data-id="element-464">
            {profile.verifications.filter(v => v.title !== 'Identity Verified').map((verification, index) => <div key={index} className="badge badge-primary flex items-center mr-1 mb-1" data-id="element-465">
                  <ShieldIcon size={12} className="mr-1" data-id="element-466" />
                  <span data-id="element-467">{verification.title}</span>
                </div>)}
          </div>}
        {/* Achievements */}
        {profile.achievements && profile.achievements.length > 0 && <div className="flex flex-wrap mt-2" data-id="element-468">
            {profile.achievements.map((achievement, index) => <div key={index} className="badge badge-accent flex items-center mr-1 mb-1" data-id="element-469">
                <AwardIcon size={12} className="mr-1" data-id="element-470" />
                <span data-id="element-471">{achievement.title}</span>
              </div>)}
          </div>}
        {/* Stats */}
        <div className="flex justify-around mt-4 bg-gray-50 rounded-xl p-3" data-id="element-472">
          <div className="text-center" data-id="element-473">
            <p className="font-bold text-green-600" data-id="element-474">
              {profile.stats.recommendations}
            </p>
            <p className="text-xs text-gray-500" data-id="element-475">Recommendations</p>
          </div>
          <div className="text-center border-x border-gray-200 px-4" data-id="element-476">
            <p className="font-bold text-green-600" data-id="element-477">
              {profile.stats.postViews?.toLocaleString() || 0}
            </p>
            <p className="text-xs text-gray-500" data-id="element-478">Post Views</p>
          </div>
          <div className="text-center border-r border-gray-200 px-4" data-id="element-479">
            <p className="font-bold text-green-600" data-id="element-480">
              {profile.stats.followers.toLocaleString()}
            </p>
            <p className="text-xs text-gray-500" data-id="element-481">Followers</p>
          </div>
          <div className="text-center" data-id="element-482">
            <p className="font-bold text-green-600" data-id="element-483">
              {profile.stats.solicitations.toLocaleString()}
            </p>
            <p className="text-xs text-gray-500" data-id="element-484">Solicitations</p>
          </div>
        </div>
      </div>
    </div>;
};
export default ProfileHeader;